import { z } from 'zod';

const optStr = (max = 200) => z.preprocess(
    (v) => (v === null || v === undefined ? undefined : v),
    z.string().max(max).optional().or(z.literal(''))
);

export const productProcessConfigSchema = z.object({
    processId: z.string(),
    setupTimeMinutes: z.coerce.number().optional().nullable(),
    speedPerHour: z.coerce.number().optional().nullable(),
    runWastePercent: z.coerce.number().optional().nullable(),
    setupWasteQty: z.coerce.number().optional().nullable(),
    isOutsourced: z.boolean().default(false).optional(),
    unitCost: z.coerce.number().optional().nullable(),
    batchCost: z.coerce.number().optional().nullable(),
    moq: z.coerce.number().optional().nullable(),
});

export const productMaterialSchema = z.object({
    materialId: z.string(),
    processId: optStr(),
    standardQuantity: z.coerce.number().default(0).optional(),
    note: optStr(),
});

export const productComponentSchema = z.object({
    id: z.string().optional(),
    componentName: z.string().min(1, 'Tên thành phần bắt buộc').max(200),
    quantity: z.coerce.number().min(1).default(1),
    unit: optStr(20),
    materialId: optStr(),
    materialIds: z.array(z.string()).optional().default([]),
    productMaterials: z.array(productMaterialSchema).optional().default([]),
    processIds: z.array(z.string()).optional().default([]),
    processConfigs: z.array(productProcessConfigSchema).optional().default([]),
    length: z.coerce.number().min(0, 'Chiều dài phải >= 0').optional(),
    width: z.coerce.number().min(0, 'Chiều rộng phải >= 0').optional(),
    height: z.coerce.number().min(0, 'Chiều cao phải >= 0').optional(),
    flatLength: z.coerce.number().min(0, 'Khổ trải dài phải >= 0').optional(),
    flatWidth: z.coerce.number().min(0, 'Khổ trải rộng phải >= 0').optional(),
    printFormat: optStr(100),
    cutFormat: optStr(100),
    process: optStr(1000),
    specs: z.any().nullable().optional(),
});

// Form schema
export const productFormSchema = z.object({
    productCode: z.preprocess(
        (v) => (v === null || v === undefined ? undefined : v),
        z.string()
            .max(30, 'Mã SP tối đa 30 ký tự')
            .refine(
                (val) => !val || /^[a-zA-Z0-9\-_]+$/.test(val.trim()),
                'Mã sản phẩm không được chứa khoảng trắng và ký tự đặc biệt'
            )
            .optional()
            .or(z.literal(''))
    ),
    productName: z.string().min(1, 'Tên SP không được để trống').max(200, 'Tối đa 200 ký tự'),
    customerId: optStr(),
    customerName: optStr(),
    typeId: optStr(),
    materialIds: z.array(z.string()).optional().default([]),
    productMaterials: z.array(productMaterialSchema).optional().default([]),
    processIds: z.array(z.string()).optional().default([]),
    processConfigs: z.array(productProcessConfigSchema).optional().default([]),
    unit: z.string().min(1, 'Đơn vị tính bắt buộc chọn').max(20, 'Tối đa 20 ký tự'),
    packingSpec: optStr(1000),
    notes: optStr(2000),
    pcsPerBox: z.preprocess(
        (v) => (v === '' || v === null || v === undefined ? null : Number(v)),
        z.number().int().positive('Số cái / thùng phải lớn hơn 0').nullable().optional()
    ),
    price: z.coerce.number().min(0, 'Giá bán phải >= 0').optional(),
    costPrice: z.coerce.number().min(0, 'Giá vốn phải >= 0').optional(),
    process: optStr(1000),
    specs: z.any().nullable().optional(),
    length: z.coerce.number().min(0, 'Chiều dài phải >= 0').optional().default(0),
    width: z.coerce.number().min(0, 'Chiều rộng phải >= 0').optional().default(0),
    height: z.coerce.number().min(0, 'Chiều cao phải >= 0').optional().default(0),
    flatLength: z.coerce.number().min(0, 'Khổ trải dài phải >= 0').optional(),
    flatWidth: z.coerce.number().min(0, 'Khổ trải rộng phải >= 0').optional(),
    printFormat: optStr(100),
    cutFormat: optStr(100),
    outs: z.coerce.number().min(1, 'Số lượng bù (outs) phải >= 1').optional(),
    image: optStr(500000),
    tags: optStr(500),
    active: z.boolean(),
    isComposite: z.boolean().default(false),
    components: z.array(productComponentSchema).optional().default([]),
});

export type ProductComponentData = z.infer<typeof productComponentSchema>;
export type ProductFormData = z.infer<typeof productFormSchema>;

export interface Product {
    id: string;
    productCode: string;
    productName: string;
    customerId?: string | null;
    customerName?: string | null;
    typeId?: string | null;
    materialIds?: string[];
    unit?: string | null;
    packingSpec?: string | null;
    notes?: string | null;
    pcsPerBox?: number | null;
    price?: number | null;
    costPrice?: number | null;
    lastPrice?: number | null;
    process?: string | null;
    specs?: any;
    image?: string | null;
    tags?: string | null;
    active: boolean;
    customer?: { id: string; name: string } | null;
    productType?: { id: string; name: string } | null;
    material?: { id: string; name: string } | null;
    createdAt: string;
    updatedAt: string;
}
